import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Empty, Input, Segmented, Table, Tag, Tooltip, Typography } from 'antd';
import { TruckOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFetchData } from '../../../hooks';
import axiosInstance from '../../../lib/axiosInstance';
import { PageableResponse } from '../../../types';
import { convertDate, dateFormat } from '../../../lib/utils';

const { Text } = Typography;

/** One receipt, seen from the supplier's delivery rather than from our stock. */
interface ReceiptRow {
  id: string;
  code?: string;
  drNo?: string | null;
  invoiceNo?: string | null;
  receivedDate?: string | null;
  status?: string;
  source?: string;
  vendor?: { id?: string; description?: string } | null;
  destination?: { id?: string; description?: string } | null;
}

/** A line on the delivery, as the receipt records it. */
interface MovementRow {
  id: string;
  quantity?: number;
  unitPrice?: number;
  product?: { code?: string; description?: string } | null;
  productUom?: { description?: string } | null;
  description?: string | null;
}

interface ReceiptDetail extends ReceiptRow {
  movements?: MovementRow[];
  notes?: string | null;
  total?: number;
}

const statusColour: Record<string, string> = {
  DRAFT: 'default',
  CONFIRM: 'processing',
  APPROVED: 'green',
  DONE: 'green',
  CANCEL: 'red',
};

/**
 * Supplier deliveries — the supplier's own delivery receipt, not ours.
 *
 * Two documents are called a DR here. An Internal DR is ours, going out to the
 * requestor. This screen is the other one: the paper that arrives with the
 * goods, carrying the supplier's DR number and invoice number.
 *
 * It is a view over receiving reports rather than a document of its own,
 * because that is where the three fields live. Recording a delivery means
 * opening its receipt, so there is one place to type and one place to look --
 * a second entry form would only mean the same number typed twice.
 */
const SupplierDeliveriesPage: React.FC = () => {
  const [view, setView] = useState<'recorded' | 'missing' | 'all'>('recorded');
  const [search, setSearch] = useState('');
  // Opened rows, fetched one at a time. The list endpoint carries no lines, and
  // pulling every receipt's contents up front to show a handful would be waste.
  const [details, setDetails] = useState<Record<string, ReceiptDetail | 'loading'>>({});

  const loadDetail = useCallback(
    async (id: string) => {
      if (details[id]) return;
      setDetails((d) => ({ ...d, [id]: 'loading' }));
      try {
        const { data } = await axiosInstance.get(`/stock/rrs/${id}`);
        setDetails((d) => ({ ...d, [id]: data as ReceiptDetail }));
      } catch {
        // Leave it unset so opening the row again retries rather than sticking
        // on a spinner that never resolves.
        setDetails((d) => {
          const next = { ...d };
          delete next[id];
          return next;
        });
      }
    },
    [details],
  );

  const { data, isLoading } = useFetchData<PageableResponse<ReceiptRow>>(
    '/stock/rrs',
    { length: 999999, from: '2000-01-01', to: '2999-12-31' },
  );

  const receipts = useMemo(() => data?.data ?? [], [data]);

  const rows = useMemo(() => {
    const byView = receipts.filter((r) =>
      view === 'all' ? true : view === 'recorded' ? !!r.drNo : !r.drNo,
    );
    const q = search.trim().toLowerCase();
    if (!q) return byView;
    return byView.filter((r) =>
      [r.drNo, r.invoiceNo, r.code, r.vendor?.description, r.source]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [receipts, view, search]);

  const recorded = receipts.filter((r) => r.drNo).length;
  const missing = receipts.length - recorded;

  return (
    <Card
      bordered={false}
      title={
        <span>
          <TruckOutlined style={{ marginRight: 8 }} />
          Supplier Deliveries
          <Tooltip title="The supplier's own delivery receipt, which arrives with the goods. An Internal DR is the opposite direction — ours, going out to the requestor.">
            <Text type="secondary" style={{ marginLeft: 10, fontSize: 12, cursor: 'help' }}>
              inbound, from the supplier
            </Text>
          </Tooltip>
        </span>
      }
      extra={
        <Segmented
          value={view}
          onChange={(v) => setView(v as typeof view)}
          options={[
            { label: `Recorded (${recorded})`, value: 'recorded' },
            { label: `Missing DR (${missing})`, value: 'missing' },
            { label: `All (${receipts.length})`, value: 'all' },
          ]}
        />
      }
    >
      <div className={'mb-3'}>
        <Text type="secondary">
          <strong>Click any row</strong> to see what was delivered. The DR number
          and receipt code open the receiving report, where these details are
          recorded.
        </Text>
      </div>

      <Input.Search
        allowClear
        placeholder="Search DR number, invoice, supplier or receipt"
        style={{ maxWidth: 420, marginBottom: 16 }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {!isLoading && rows.length === 0 ? (
        <Empty
          description={
            view === 'missing'
              ? 'Every receipt has a supplier DR recorded.'
              : view === 'recorded'
                ? 'No supplier DR has been recorded yet. Open a receipt to add one.'
                : 'No receipts.'
          }
        />
      ) : (
        <Table<ReceiptRow>
          rowKey={(r) => r.id}
          dataSource={rows}
          loading={isLoading}
          pagination={{ showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
          rowClassName={() => 'cursor-pointer'}
          expandable={{
            expandRowByClick: true,
            onExpand: (open, r) => {
              if (open) loadDetail(r.id);
            },
            expandedRowRender: (r) => {
              const d = details[r.id];
              if (!d || d === 'loading') {
                return <Text type="secondary">Loading what was delivered…</Text>;
              }
              const lines = d.movements ?? [];
              if (lines.length === 0) {
                return <Text type="secondary">This receipt records no lines.</Text>;
              }
              return (
                <div>
                  <div className={'mb-2'}>
                    <Text type="secondary">
                      Received into {d.destination?.description ?? '—'}
                      {d.source ? ` · against ${d.source}` : ''}
                      {d.notes ? ` · ${d.notes}` : ''}
                    </Text>
                  </div>
                  <Table<MovementRow>
                    size="small"
                    rowKey={(m) => m.id}
                    dataSource={lines}
                    pagination={false}
                  >
                    <Table.Column
                      title="Material"
                      render={(_t, m: MovementRow) =>
                        m.product?.description ?? m.description ?? '—'
                      }
                    />
                    <Table.Column
                      title="Code"
                      render={(_t, m: MovementRow) => m.product?.code ?? '—'}
                    />
                    <Table.Column
                      title="Quantity"
                      align="right"
                      render={(_t, m: MovementRow) =>
                        `${m.quantity ?? 0} ${m.productUom?.description ?? ''}`.trim()
                      }
                    />
                    <Table.Column
                      title="Unit price"
                      align="right"
                      render={(_t, m: MovementRow) =>
                        m.unitPrice != null ? m.unitPrice.toLocaleString() : '—'
                      }
                    />
                  </Table>
                </div>
              );
            },
          }}
        >
          <Table.Column
            title="Supplier DR No."
            dataIndex="drNo"
            render={(_t, r: ReceiptRow) =>
              r.drNo ? (
                // stopPropagation so following the link does not also toggle
                // the row open underneath it.
                <Link
                  to={`/stock-movement/receiving-report/${r.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {r.drNo}
                </Link>
              ) : (
                <Tooltip title="Goods were received with no supplier DR number recorded">
                  <Text type="warning">
                    <WarningOutlined /> not recorded
                  </Text>
                </Tooltip>
              )
            }
          />
          <Table.Column
            title="Received"
            dataIndex="receivedDate"
            sorter={(a: ReceiptRow, b: ReceiptRow) =>
              dayjs(a.receivedDate ?? 0).valueOf() - dayjs(b.receivedDate ?? 0).valueOf()
            }
            render={(_t, r: ReceiptRow) =>
              r.receivedDate ? (
                convertDate(r.receivedDate, dateFormat)
              ) : (
                <Text type="secondary">—</Text>
              )
            }
          />
          <Table.Column
            title="Supplier"
            dataIndex="vendor"
            render={(_t, r: ReceiptRow) =>
              r.vendor?.description ?? <Text type="secondary">—</Text>
            }
          />
          <Table.Column
            title="Invoice No."
            dataIndex="invoiceNo"
            render={(_t, r: ReceiptRow) =>
              r.invoiceNo ?? <Text type="secondary">—</Text>
            }
          />
          <Table.Column
            title="Receipt"
            dataIndex="code"
            render={(_t, r: ReceiptRow) => (
              // The fields are edited on the receipt, so that is where this goes.
              <Link
                to={`/stock-movement/receiving-report/${r.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                {r.code ?? `#${r.id}`}
              </Link>
            )}
          />
          <Table.Column
            title="Status"
            dataIndex="status"
            render={(_t, r: ReceiptRow) => (
              <Tag color={statusColour[r.status ?? 'DRAFT']}>{r.status}</Tag>
            )}
          />
        </Table>
      )}
    </Card>
  );
};

export default SupplierDeliveriesPage;
